import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import {
    useTransphereStatus,
    encaissementInProgressStatusOf,
    type TransphereBannerVariant,
    type EncaissementInProgressStatus,
} from '../../hooks/useTransphereStatus';
import { useIsAcomba } from '../../hooks/useIsAcomba';
import {
    openTransphereConfigInPortal,
    openTransphereMarketingSite,
    type TransphereConfigTarget,
} from '../../utils/portal/openInPortal';
import { BUTTON_BASE_CHROME, SIZE_CLASSES } from '../../ui/Button/Button';
import { Banner } from '../../ui/Banner/Banner';
import { TransphereContactModal } from './TransphereContactModal';
import { TransphereLogo } from './TransphereLogo';

/** The card sits on a filled primary surface, where neither Button variant reads: the action is a
 * hollow outline in the card's own on-primary colour instead. */
const ON_DARK_OUTLINE_BUTTON =
    `${SIZE_CLASSES.md} ${BUTTON_BASE_CHROME} ml-auto inline-flex w-fit cursor-pointer items-center ` +
    'justify-center border border-current text-compact font-semibold hover:bg-primary-1000 active:bg-primary-1100';

/** The three card variants — everything but the in-progress sub-states, which render as a status
 * `Banner` instead of this promotional card. */
type CardVariant = Exclude<TransphereBannerVariant, `encaissement-in-progress-${string}`>;

interface VariantCopy {
    title: string;
    message: string;
    /** The state's own action, or null for one that offers nothing to activate. */
    action: {
        /** Where the primary action deep-links in the AcombaGO portal. */
        target: TransphereConfigTarget;
        actionLabel: string;
        actionTestId: string;
        /** What the desktop contact modal says instead, having no portal to open. */
        contactTitle: string;
        contactMessage: string;
    } | null;
}

const COPY: Record<CardVariant, VariantCopy> = {
    'not-subscribed': {
        title: TranslationKey.Home.TransphereBanner.NotSubscribedTitle,
        message: TranslationKey.Home.TransphereBanner.NotSubscribedMessage,
        action: {
            target: 'activate',
            actionLabel: TranslationKey.Home.TransphereBanner.ActivationAction,
            actionTestId: 'transphere-banner-activation-btn',
            contactTitle: TranslationKey.Home.TransphereBanner.ContactTitle,
            contactMessage: TranslationKey.Home.TransphereBanner.ContactMessage,
        },
    },
    'subscribed-no-encaissement': {
        title: TranslationKey.Home.TransphereBanner.SubscribedTitle,
        message: TranslationKey.Home.TransphereBanner.SubscribedMessage,
        action: {
            target: 'subscribe-encaissement',
            actionLabel: TranslationKey.Home.TransphereBanner.SubscriptionAction,
            actionTestId: 'transphere-banner-subscription-btn',
            contactTitle: TranslationKey.Home.TransphereBanner.ContactEncaissementTitle,
            contactMessage: TranslationKey.Home.TransphereBanner.ContactEncaissementMessage,
        },
    },
    // Nothing to activate: the subscription exists and is not taking payments, so the card only reports
    // it, and the message itself points to the ACCEO representative.
    'encaissement-subscription-inactive': {
        title: TranslationKey.Home.TransphereBanner.SubscriptionInactiveTitle,
        message: TranslationKey.Home.TransphereBanner.SubscriptionInactiveMessage,
        action: null,
    },
};

const IN_PROGRESS_COPY: Record<EncaissementInProgressStatus, { title: string; message: string }> = {
    processing: {
        title: TranslationKey.Home.TransphereBanner.InProgressProcessingTitle,
        message: TranslationKey.Home.TransphereBanner.InProgressProcessingMessage,
    },
    pending: {
        title: TranslationKey.Home.TransphereBanner.InProgressPendingTitle,
        message: TranslationKey.Home.TransphereBanner.InProgressPendingMessage,
    },
    incomplete: {
        title: TranslationKey.Home.TransphereBanner.InProgressIncompleteTitle,
        message: TranslationKey.Home.TransphereBanner.InProgressIncompleteMessage,
    },
    rejected: {
        title: TranslationKey.Home.TransphereBanner.InProgressRejectedTitle,
        message: TranslationKey.Home.TransphereBanner.InProgressRejectedMessage,
    },
};

/**
 * Transphere card, rendered after the relance list/tabs (see Home.tsx).
 *
 * Desktop (Acomba bridge) sessions render the same card with a "Nous contacter" action instead of
 * "En savoir plus": there is no channel for the embedded page to open Acomba's own Configuration
 * compagnie screen, or to reach the AcombaGO portal it would otherwise deep-link into, so activation
 * there is a human conversation with an ACCEO representative rather than a self-serve flow.
 */
export const TransphereBanner: React.FC = () => {
    const { t } = useTranslation();
    const variant = useTransphereStatus();
    const isAcomba = useIsAcomba();
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);

    if (variant === null) return null;

    const inProgressStatus = encaissementInProgressStatusOf(variant);
    if (inProgressStatus) {
        const copy = IN_PROGRESS_COPY[inProgressStatus];
        return (
            <Banner
                tone="info"
                testId="transphere-banner-in-progress"
                title={t(copy.title)}
            >
                {t(copy.message)}
            </Banner>
        );
    }

    // Destructured rather than read through COPY[variant]: the action's own closures would otherwise
    // lose the null check on a property access TypeScript cannot see through.
    const { title, message, action } = COPY[variant as CardVariant];

    return (
        <div
            data-testid="transphere-banner"
            className="flex flex-col gap-m rounded-lg bg-primary-900 px-xl py-l text-content-on-primary shadow-weak"
        >
            <TransphereLogo
                label={t(TranslationKey.Home.TransphereBanner.BrandName)}
                className="h-6 w-auto self-start"
            />

            <p className="text-m">
                <span data-testid="transphere-banner-title">{t(title)}</span>{' '}
                <span data-testid="transphere-banner-message">{t(message)}</span>
            </p>

            {/* openTransphereConfigInPortal is a no-op outside the AcombaGO iframe (portal-embedded check
                lives in the postMessage helper itself) — desktop has no parent iframe to receive it, so it
                uses the contact modal instead of the portal deep-link. The external marketing link only
                makes sense there too: desktop has nothing to send a user browsing away from Acomba to. */}
            {action && (isAcomba ? (
                <button
                    type="button"
                    data-testid="transphere-banner-contact"
                    onClick={() => setIsContactModalOpen(true)}
                    className={ON_DARK_OUTLINE_BUTTON}
                >
                    {t(action.actionLabel)}
                </button>
            ) : (
                <div className="ml-auto flex w-fit items-center gap-m">
                    <button
                        type="button"
                        data-testid="transphere-banner-learn-more-btn"
                        onClick={openTransphereMarketingSite}
                        className="cursor-pointer rounded-sm text-s font-normal underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-focus-ring"
                    >
                        {t(TranslationKey.Home.TransphereBanner.LearnMore)}
                    </button>
                    <button
                        type="button"
                        data-testid={action.actionTestId}
                        onClick={() => openTransphereConfigInPortal(action.target)}
                        className={ON_DARK_OUTLINE_BUTTON}
                    >
                        {t(action.actionLabel)}
                    </button>
                </div>
            ))}

            {isContactModalOpen && action && (
                <TransphereContactModal
                    heading={t(action.contactTitle)}
                    message={t(action.contactMessage)}
                    onClose={() => setIsContactModalOpen(false)}
                />
            )}
        </div>
    );
};
